import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Download, Gift, Upload, WalletCards, KeyRound, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Three player actions inside the game page:
 *   - LOAD: add dollars to your game balance
 *   - REDEEM: ask operator to cash out dollars
 *   - RESET PASSWORD: issue a new game password through provider automation
 */
const GAME_LOAD_MAX_EXISTING_BALANCE = 5;
const GAME_LOAD_MIN_DOLLARS = 5;

function parseGameBalance(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const clean = String(value).replace(/[$,]/g, "").trim();
  const direct = Number(clean);
  if (Number.isFinite(direct)) return direct;
  const match = clean.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function errorText(error) {
  return [
    error?.response?.data,
    error?.response?.data?.detail,
    error?.message,
  ]
    .map((value) => (typeof value === "string" ? value : value ? JSON.stringify(value) : ""))
    .join(" ")
    .toLowerCase();
}

function isBalanceRefreshError(errorOrMessage) {
  const text = typeof errorOrMessage === "string" ? errorOrMessage.toLowerCase() : errorText(errorOrMessage);
  return [
    "could not verify current game balance",
    "could not read the current game balance",
    "could not be verified on the provider balance",
    "provider response was unclear",
    "game connection is being refreshed",
    "live game balance is refreshing",
    "live balance is refreshing",
    "live sync retrying",
    "temporarily_unavailable",
  ].some((needle) => text.includes(needle));
}

function isSafeBalanceError(errorOrMessage) {
  const text = typeof errorOrMessage === "string" ? errorOrMessage.toLowerCase() : errorText(errorOrMessage);
  return (
    text.includes("safe balance") ||
    text.includes("safe_balance") ||
    text.includes("bank balance") ||
    text.includes("vault balance")
  );
}

function playerActionError(error, action) {
  const message = error?.message && !error?.response
    ? error.message
    : formatApiError(error, { audience: "player" });
  if (action === "redeem" && (isSafeBalanceError(error) || isSafeBalanceError(message))) {
    return {
      tone: "warning",
      message,
    };
  }
  if (isBalanceRefreshError(error) || isBalanceRefreshError(message)) {
    return {
      tone: "info",
      message: action === "load"
        ? "Load was sent, and the live game balance is still refreshing. Try Refresh balance again in a moment."
        : "Live game balance is refreshing. Try again in a moment.",
    };
  }
  return { tone: "error", message };
}

export default function GameActionsBar({
  gameId,
  game,
  onRequestCreated,
  onLoadComplete,
  automationConnected = false,
  providerBalance = null,
  providerBalanceLoaded = false,
}) {
  const [open, setOpen] = useState(null); // "load" | "redeem" | "reset" | null
  const { user } = useAuth();
  const nav = useNavigate();
  const numericProviderBalance = parseGameBalance(providerBalance);
  const loadBlockedByGameBalance =
    automationConnected &&
    providerBalanceLoaded &&
    numericProviderBalance !== null &&
    numericProviderBalance > GAME_LOAD_MAX_EXISTING_BALANCE;

  const openAction = (action) => {
    if (!user || user === false) {
      toast.info("Please sign in again.");
      nav("/login");
      return;
    }
    if (action === "load" && loadBlockedByGameBalance) {
      toast.info(`Load is available only when game balance is $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)} or less.`);
      return;
    }
    setOpen(action);
  };

  return (
    <>
      {loadBlockedByGameBalance && (
        <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Load is hidden because this game account has ${numericProviderBalance.toFixed(2)} inside the game. Load again after the game balance is ${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)} or less.
        </div>
      )}
      <div className={`mt-6 grid grid-cols-1 gap-3 ${loadBlockedByGameBalance ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} data-testid="game-actions-bar">
        {!loadBlockedByGameBalance && (
          <ActionPill
            icon={Download}
            label="Load"
            sub="Add dollars"
            color="emerald"
            testid="action-load"
            onClick={() => openAction("load")}
          />
        )}
        <ActionPill
          icon={Upload}
          label="Redeem"
          sub="Cash out"
          color="gold"
          testid="action-redeem"
          onClick={() => openAction("redeem")}
        />
        <ActionPill
          icon={KeyRound}
          label="Reset password"
          sub="Once per 24 hours"
          color="slate"
          testid="action-reset"
          onClick={() => openAction("reset")}
        />
      </div>

      {open === "load" && (
        <LoadDialog
          gameId={gameId}
          game={game}
          onLoadComplete={onLoadComplete}
          onClose={() => setOpen(null)}
          loadBlockedByGameBalance={loadBlockedByGameBalance}
          providerBalance={providerBalance}
        />
      )}
      {open === "redeem" && (
        <RedeemDialog
          gameId={gameId}
          game={game}
          onLoadComplete={onLoadComplete}
          onClose={() => setOpen(null)}
          providerBalance={providerBalance}
          providerBalanceLoaded={providerBalanceLoaded}
        />
      )}
      {open === "reset" && <ResetPasswordDialog gameId={gameId} onRequestCreated={onRequestCreated} onClose={() => setOpen(null)} />}
    </>
  );
}

function ActionPill({ icon: Icon, label, sub, color, onClick, testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100",
    gold: "border-[#2F6FA5]/25 bg-[#EAF4FC] text-[#2F6FA5] hover:border-[#2F6FA5]/60 hover:bg-[#dbeefa]",
    slate: "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
  };
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`group flex min-w-0 items-center gap-3 rounded-xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 ${tones[color]}`}
    >
      <div className="w-10 h-10 rounded-lg bg-white/75 border border-current/15 grid place-items-center shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0 text-left">
        <div className="font-display font-bold text-base leading-tight break-words">{label}</div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500 break-words">{sub}</div>
      </div>
    </button>
  );
}

/* ---------- Dialog shell ---------- */
function Dialog({ title, subtitle, onClose, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-[90] overflow-y-auto overscroll-contain bg-slate-900/35 p-3 py-4 pb-[calc(6.5rem+env(safe-area-inset-bottom))] backdrop-blur-sm sm:grid sm:place-items-center sm:p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
        className="mx-auto w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 pb-8 text-slate-950 shadow-2xl sm:p-7"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="font-display text-xl font-bold leading-tight">{title}</h3>
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- LOAD ---------- */
function LoadDialog({ gameId, game, onLoadComplete, onClose, loadBlockedByGameBalance = false, providerBalance = null }) {
  const { user, refreshUser } = useAuth();
  const [amount, setAmount] = useState(10);
  const [fundingSource, setFundingSource] = useState("wallet");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const walletDollars = (Number(user?.coins || 0) / 1000);
  const freePlayDollars = (Number(user?.free_play || 0) / 1000);
  const availableDollars = walletDollars + freePlayDollars;
  const loadDollars = Number(amount || 0);
  const walletAmount = Math.round(loadDollars * 1000);
  const firstBonus = Number(game?.first_deposit_bonus_percent ?? 100);
  const regularBonus = Number(game?.regular_deposit_bonus_percent ?? 20);
  const minMultiplier = Number(game?.min_load_multiplier ?? 4);
  const maxMultiplier = Number(game?.max_load_multiplier ?? 10);
  const minRedeem = Number(game?.min_redeem ?? 20);
  const maxRedeem = Number(game?.max_redeem ?? 1000);
  const redeemFee = Number(game?.redeem_fee_percent ?? 5);
  const selectedBalanceDollars = fundingSource === "freeplay" ? freePlayDollars : walletDollars;
  const sourceTitle = fundingSource === "freeplay" ? "Freeplay bonus" : "Wallet money";
  const projectedFreePlayDollars = fundingSource === "freeplay"
    ? Math.min(Number.isFinite(loadDollars) && loadDollars > 0 ? loadDollars : 0, Math.max(0, freePlayDollars))
    : 0;
  const projectedCashDollars = fundingSource === "wallet" && Number.isFinite(loadDollars) && loadDollars > 0 ? loadDollars : 0;
  const projectedRuleBaseDollars = fundingSource === "freeplay" ? projectedFreePlayDollars : projectedCashDollars;
  const projectedPaidRedeemMin = projectedRuleBaseDollars > 0 ? Math.max(minRedeem, Math.ceil(projectedRuleBaseDollars * minMultiplier)) : minRedeem;
  const projectedPaidRedeemMax = projectedRuleBaseDollars > 0 ? Math.min(maxRedeem, Math.floor(projectedRuleBaseDollars * maxMultiplier)) : maxRedeem;
  const projectedRedeemMin = projectedPaidRedeemMin;
  const projectedRedeemMax = projectedPaidRedeemMax;
  useEffect(() => {
    if (freePlayDollars <= 0 && fundingSource === "freeplay") setFundingSource("wallet");
  }, [freePlayDollars, fundingSource]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (!Number.isFinite(loadDollars) || loadDollars <= 0) {
        throw new Error("Enter a valid dollar amount.");
      }
      if (loadDollars < GAME_LOAD_MIN_DOLLARS) {
        throw new Error(`Minimum game load is $${GAME_LOAD_MIN_DOLLARS.toFixed(2)}.`);
      }
      if (loadBlockedByGameBalance) {
        const shownBalance = parseGameBalance(providerBalance);
        throw new Error(
          shownBalance !== null
            ? `Load is available only when game balance is $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)} or less. Current game balance is $${shownBalance.toFixed(2)}.`
            : `Load is available only when game balance is $${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)} or less.`,
        );
      }
      if (loadDollars > selectedBalanceDollars) {
        throw new Error(`Load amount is higher than your ${sourceTitle.toLowerCase()} balance.`);
      }
      const { data } = await api.post("/automation/load", {
        gameName: gameId,
        amount: Math.round(loadDollars),
        walletAmount,
        fundingSource,
        remark: "website game load",
      });
      await refreshUser?.();
      await onLoadComplete?.(data);
      if (data?.pending_review) {
        toast.success(data?.message || "Load is processing. Support will review it shortly.");
        onClose();
        return;
      }
      const bonus = Number(data?.bonus_amount || 0);
      const loaded = Number(data?.provider_amount || data?.amount || loadDollars);
      const usedFreeplay = Number(data?.free_play_debit_amount || 0) > 0;
      toast.success(`${usedFreeplay ? "Freeplay" : "Wallet"} loaded ${loaded.toLocaleString()} to game${bonus ? ` (${bonus.toLocaleString()} bonus)` : ""}`);
      onClose();
    } catch (e) {
      const nextErr = playerActionError(e, "load");
      setErr(nextErr);
      if (nextErr.tone === "info") toast.info(nextErr.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Load to game" subtitle="Choose wallet money or freeplay before loading this game" onClose={onClose} testid="load-dialog">
      {user && (
        <div className="grid grid-cols-1 gap-1 text-xs text-slate-500 mb-4 sm:grid-cols-3">
          <div>Wallet: <span className="text-slate-950 font-medium tabular-nums">${walletDollars.toFixed(2)}</span></div>
          <div>Freeplay: <span className="text-[#2F6FA5] font-medium tabular-nums">${freePlayDollars.toFixed(2)}</span></div>
          <div>Available: <span className="text-emerald-700 font-medium tabular-nums">${availableDollars.toFixed(2)}</span></div>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4">
        {freePlayDollars > 0 && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Load source</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setFundingSource("wallet")}
                data-testid="load-source-wallet"
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                  fundingSource === "wallet" ? "border-[#2F6FA5] bg-white text-slate-950 shadow-sm" : "border-slate-200 bg-white/70 text-slate-600 hover:bg-white"
                }`}
              >
                <span className="flex items-center gap-2 font-semibold"><WalletCards className="h-4 w-4" /> Wallet only</span>
                <span className="tabular-nums">${walletDollars.toFixed(2)}</span>
              </button>
              <button
                type="button"
                onClick={() => setFundingSource("freeplay")}
                data-testid="load-source-freeplay"
                className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm transition ${
                  fundingSource === "freeplay" ? "border-[#2F6FA5] bg-white text-slate-950 shadow-sm" : "border-slate-200 bg-white/70 text-slate-600 hover:bg-white"
                }`}
              >
                <span className="flex items-center gap-2 font-semibold"><Gift className="h-4 w-4" /> Freeplay only</span>
                <span className="tabular-nums">${freePlayDollars.toFixed(2)}</span>
              </button>
            </div>
          </div>
        )}
        <Field label="Amount ($)">
          <input
            type="number" required min={GAME_LOAD_MIN_DOLLARS} step={1} value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="load-amount"
            className={inp}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          {[5, 10, 20, 50, 100].map((v) => (
            <button key={v} type="button" onClick={() => setAmount(v)}
              data-testid={`load-quick-${v}`}
              className="px-3 py-1 text-xs rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50">
              ${v}
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] p-3 text-xs leading-relaxed text-slate-700">
          {fundingSource === "wallet"
            ? `Wallet money loads only from paid balance. Freeplay stays in your account unless you choose Freeplay only. Cash wallet loads get ${firstBonus}% first bonus, then ${regularBonus}% regular bonus.`
            : `Freeplay loads from bonus balance at face value with no load bonus. Freeplay cashout follows ${minMultiplier}x minimum to ${maxMultiplier}x maximum of the freeplay amount loaded; the manual deduction percent set by admin is applied at redeem.`}
          <div className="mt-3 grid gap-2 rounded-lg border border-[#2F6FA5]/15 bg-white/55 p-2 text-slate-800">
            <div className="flex items-center justify-between gap-3">
              <span>{fundingSource === "freeplay" ? "Freeplay load minimum" : "Wallet load minimum"}</span>
              <span className="font-semibold tabular-nums">${GAME_LOAD_MIN_DOLLARS.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>{fundingSource === "freeplay" ? "Freeplay load maximum" : "Wallet load maximum"}</span>
              <span className="font-semibold tabular-nums">${Math.max(0, selectedBalanceDollars).toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Cashout minimum</span>
              <span className="font-semibold tabular-nums">${projectedRedeemMin.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span>Cashout maximum</span>
              <span className="font-semibold tabular-nums">${projectedRedeemMax.toFixed(2)}</span>
            </div>
            {fundingSource === "freeplay" ? (
              <p className="text-[11px] text-slate-500">
                Example: ${projectedFreePlayDollars.toFixed(2)} Freeplay load can redeem from ${projectedRedeemMin.toFixed(2)} to ${projectedRedeemMax.toFixed(2)} only. Eligible Freeplay redemption deducts 60%; 40% is credited to your wallet.
              </p>
            ) : (
              <>
                <p className="text-[11px] text-slate-500">
                  Load is allowed only when the game account balance is ${GAME_LOAD_MAX_EXISTING_BALANCE.toFixed(2)} or less; ${(
                    GAME_LOAD_MAX_EXISTING_BALANCE + 0.01
                  ).toFixed(2)} or more is blocked. For this paid load, cashout is {minMultiplier}x minimum to {maxMultiplier}x maximum before the {redeemFee}% redeem fee.
                </p>
                <p className="text-[11px] text-slate-500">
                  If your game balance is above the cashout maximum, redeem the full game balance; the extra is voided by rules, then the redeem fee is deducted from the eligible amount before wallet credit.
                </p>
              </>
            )}
          </div>
          {(projectedFreePlayDollars > 0 || projectedCashDollars > 0) && (
            <span className="mt-2 block text-slate-800">
              This load will use {fundingSource === "freeplay" ? `$${projectedFreePlayDollars.toFixed(2)} freeplay` : `$${projectedCashDollars.toFixed(2)} wallet money`}.
            </span>
          )}
        </div>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading} data-testid="load-submit"
          className="w-full py-3 bg-emerald-600 text-white font-bold rounded-full hover:bg-emerald-500 transition-all disabled:opacity-50">
          {loading ? "Loading..." : fundingSource === "freeplay" ? "Load freeplay now" : "Load wallet now"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- REDEEM ---------- */
function RedeemDialog({ gameId, game, onLoadComplete, onClose, providerBalance = null, providerBalanceLoaded = false }) {
  const { refreshUser } = useAuth();
  const [amount, setAmount] = useState(30);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const numericProviderBalance = parseGameBalance(providerBalance);
  const wholeProviderBalance = numericProviderBalance !== null ? Math.floor(numericProviderBalance) : null;
  useEffect(() => {
    if (providerBalanceLoaded && wholeProviderBalance !== null) {
      setAmount(wholeProviderBalance);
    }
  }, [providerBalanceLoaded, wholeProviderBalance]);
  const redeemAmount = Number(amount || 0);
  const wholeRedeemAmount = Number.isFinite(redeemAmount) ? Math.floor(redeemAmount) : 0;
  const minMultiplier = Number(game?.min_load_multiplier ?? 4);
  const maxMultiplier = Number(game?.max_load_multiplier ?? 10);
  const minRedeem = Number(game?.min_redeem ?? 20);
  const maxRedeem = Number(game?.max_redeem ?? 1000);
  const redeemFee = Number(game?.redeem_fee_percent ?? 5);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (!Number.isFinite(redeemAmount) || wholeRedeemAmount <= 0) {
        throw new Error("Enter a valid redeem amount.");
      }
      const { data } = await api.post("/automation/redeem", {
        gameName: gameId,
        amount: wholeRedeemAmount,
        remark: "website game redeem",
      });
      await refreshUser?.();
      await onLoadComplete?.(data);
      if (data?.pending_admin_review) {
        toast.info(data?.message || "Redeem request sent to admin review. Support will verify the game balance and credit your wallet if approved.");
        onClose();
        return;
      }
      const credited = Number(data?.wallet_credit_amount || 0) / 1000;
      const voided = Number(data?.voided_amount || 0);
      toast.success(
        voided > 0
          ? `Redeemed $${Number(data?.amount || wholeRedeemAmount).toFixed(2)} whole dollars. $${voided.toFixed(2)} voided by rules; credited $${credited.toFixed(2)}.`
          : `Redeemed $${Number(data?.amount || wholeRedeemAmount).toFixed(2)} whole dollars from game and credited $${credited.toFixed(2)} to wallet`,
      );
      onClose();
    } catch (e) {
      const nextErr = playerActionError(e, "redeem");
      setErr(nextErr);
      if (nextErr.tone === "info") toast.info(nextErr.message);
      else if (nextErr.tone === "warning") toast.warning(nextErr.message);
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Redeem from game" subtitle="Redeem pulls the whole-dollar game balance and credits your wallet by rules" onClose={onClose} testid="redeem-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Amount">
          <input
            type="number" required min={1} step={1} value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="redeem-amount"
            className={inp}
          />
        </Field>
        <div className="flex flex-wrap gap-2">
          {[30, 50, 100, 200].map((v) => (
            <button key={v} type="button" onClick={() => setAmount(v)}
              data-testid={`redeem-quick-${v}`}
              className="px-3 py-1 text-xs rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50">
              {v}
            </button>
          ))}
        </div>
        <div className="rounded-xl border border-[#2F6FA5]/20 bg-[#EAF4FC] p-3 text-xs leading-relaxed text-slate-700">
          Paid-load redeem rule: {minMultiplier}x to {maxMultiplier}x paid load amount before bonus, ${minRedeem}-${maxRedeem}, {redeemFee}% game fee.
          <span className="mt-1 block text-slate-800">
            Example: $20 paid with a 100% bonus loads $40 to the game, but the cash-out rule still uses the $20 paid amount: minimum ${(20 * minMultiplier).toFixed(0)} and maximum ${(20 * maxMultiplier).toFixed(0)}.
          </span>
          <span className="mt-1 block text-slate-800">
            Redeem pulls whole dollars only. If the game balance is $200.65, automatic redeem pulls $200 and ignores the $0.65 points/cents.
          </span>
          <span className="mt-1 block text-slate-800">
            If the whole-dollar game balance is above the rule max, the extra amount is voided and only the allowed amount credits to wallet.
          </span>
          {["vblink", "ultrapanda"].includes(String(gameId || "").toLowerCase().replace(/[^a-z0-9]+/g, "")) && (
            <span className="mt-1 block font-semibold text-[#1B5E8A]">
              Safe/Bank balance is checked first. Move Safe balance back to main game balance before redeeming.
            </span>
          )}
          <span className="mt-1 block text-slate-800">
            Final wallet credit follows the latest approved load and the existing game limits. Paid wallet loads use the game fee. Freeplay-only, referral, phone, and leaderboard Freeplay redemptions deduct 60%; the remaining 40% is credited to your wallet.
          </span>
        </div>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading} data-testid="redeem-submit"
          className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:bg-[#4E91C8] transition-all disabled:opacity-50">
          {loading ? "Redeeming..." : "Redeem now"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- RESET PASSWORD ---------- */
function ResetPasswordDialog({ gameId, onRequestCreated, onClose }) {
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/me/game-password/reset", {
        game_id: gameId,
      });
      onRequestCreated?.(data);
      toast.success("Game password reset. Your new password is shown in Account actions.");
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <Dialog title="Reset password" subtitle="Create a new game password instantly" onClose={onClose} testid="reset-dialog">
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          You do not need the old password. This changes the password for your game account immediately. You can reset each game password once every 24 hours.
        </div>
        {err && <Err msg={err} />}
        <button type="submit" disabled={loading} data-testid="reset-submit"
          className="w-full py-3 bg-slate-950 text-white font-bold rounded-full hover:bg-slate-800 transition-all disabled:opacity-50">
          {loading ? "Resetting..." : "Reset password now"}
        </button>
      </form>
    </Dialog>
  );
}

/* ---------- shared ---------- */
const inp = "w-full px-4 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-950 placeholder:text-slate-400 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20 text-sm";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function Err({ msg }) {
  const detail = typeof msg === "string" ? { tone: "error", message: msg } : msg;
  if (detail?.tone === "info") {
    return <div className="text-sm text-[#1B5E8A] bg-[#EAF4FC] border border-[#2F6FA5]/20 rounded-lg p-3">{detail.message}</div>;
  }
  if (detail?.tone === "warning") {
    return <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">{detail.message}</div>;
  }
  return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{detail?.message || "Something went wrong."}</div>;
}
